import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
} from '@mui/material';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  open: boolean;
  workerId: number;
  skillId?: number;
  skillRequestId?: number;
  certificateType?: string;
  onClose: () => void;
  onSuccess: () => void;
};

const UploadCertificateModal = ({
  open,
  workerId,
  skillId,
  skillRequestId,
  certificateType,
  onClose,
  onSuccess,
}: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setSelectedFile(null);
      setExpirationDate('');
      setError(null);
      onClose();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Please select an image file');
      return;
    }

    if (!expirationDate) {
      setError('Please enter an expiration date');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('worker_id', workerId.toString());
      if (skillId) {
        formData.append('skill_id', skillId.toString());
      }
      if (skillRequestId) {
        formData.append('skill_request_id', skillRequestId.toString());
      }
      if (expirationDate) {
        formData.append('expiration_date', expirationDate);
      }
      // Use the certificate type from the skill, or default to 'food_handling' if not provided
      const typeToUse = certificateType || 'food_handling';
      formData.append('type', typeToUse);

      const response = await axiosRubyClient.post(
        '/api/admin/certificate_photos',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      if (response.data.success) {
        onSuccess();
        handleClose();
      } else {
        setError(
          Array.isArray(response.data.errors)
            ? response.data.errors.join(', ')
            : response.data.error || 'Failed to upload certificate',
        );
      }
    } catch (err: any) {
      console.error('Error uploading certificate:', err);
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.response?.data?.error ||
          'Failed to upload certificate',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Certificate</DialogTitle>
      <DialogContent>
        <Stack vertical gap={16} style={{ marginTop: '8px' }}>
          <Small color="lighter">Upload your certificate photo.</Small>

          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={loading}
            style={{
              padding: '12px',
              border: '1px solid #D1D5DB',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          />

          {selectedFile && (
            <Stack vertical gap={4}>
              <Small weight="semibold">Selected file:</Small>
              <Small color="lighter">{selectedFile.name}</Small>
              <Small color="lighter">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </Small>
            </Stack>
          )}

          <TextField
            label="Expiration Date"
            type="date"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            fullWidth
            required
            disabled={loading}
            InputLabelProps={{ shrink: true }}
            helperText="When does this certificate expire?"
          />

          {error && (
            <Small color="danger" style={{ marginTop: '8px' }}>
              {error}
            </Small>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedFile || !expirationDate}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadCertificateModal;
